import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import api from "../../utils/api";

const PlansModal = ({ open, onClose, onSelect, selectedPlanId }) => {
  const {
    data: apiPlans = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["plans"],
    queryFn: () => api.get("/plans").then((res) => res.data.data),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const plans = apiPlans.map((p) => ({
    id: p.id,
    name: p.name,
    price: `$${p.price}`,
    period: `/${p.billing_interval}`,
    lawyers: `${p.max_lawyers} lawyer${p.max_lawyers > 1 ? "s" : ""}`,
    forms: `${p.max_forms_per_month} form${p.max_forms_per_month > 1 ? "s" : ""}`,
    features: p.features.length > 0
      ? p.features
      : ["Technical Support", "Automatic Updates"],
    featured: p.is_active && p.sort_order === 0,
  }));

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            className="relative z-10 w-full max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8 overflow-y-auto max-h-[90vh]"
            initial={{ scale: 0.98, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.98, y: 16, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              aria-label="Close"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" stroke="currentColor" fill="none">
                <path strokeWidth="2" strokeLinecap="round" d="M6 6l12 12M18 6l-12 12" />
              </svg>
            </button>

            {isLoading && (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            )}

            {isError && (
              <div className="text-center py-8 text-red-600">
                <p>Failed to load plans</p>
                <p className="text-sm">{error?.message || "An unexpected error occurred"}</p>
              </div>
            )}

            {!isLoading && !isError && plans.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {plans.map((plan, idx) => {
                  const isSelected = selectedPlanId === plan.id;
                  return (
                    <motion.div
                      key={plan.id}
                      className={`relative bg-white rounded-xl border ${plan.featured ? "border-blue-500" : "border-gray-200"
                        } shadow-sm p-6 flex flex-col`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      {plan.featured && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs px-3 py-1 rounded-full bg-blue-600 text-white shadow">
                          Most Popular
                        </span>
                      )}

                      <div className="text-center">
                        <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                        <div className="mt-2 flex items-baseline justify-center">
                          <span className="text-3xl font-extrabold">{plan.price}</span>
                          {plan.period && (
                            <span className="ml-1 text-gray-500">{plan.period}</span>
                          )}
                        </div>
                      </div>

                      <div className="mt-5 space-y-3">
                        <div className="flex justify-between text-sm py-2 border-b">
                          <span className="text-gray-600">Lawyers</span>
                          <span className="font-medium text-gray-900">{plan.lawyers}</span>
                        </div>
                        <div className="flex justify-between text-sm py-2 border-b">
                          <span className="text-gray-600">Forms</span>
                          <span className="font-medium text-gray-900">{plan.forms}</span>
                        </div>
                      </div>

                      <ul className="mt-5 space-y-2 text-sm text-gray-700 flex-1">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-start">
                            <svg
                              className="w-4 h-4 mt-1 mr-2 text-blue-600"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                            </svg>
                            {f}
                          </li>
                        ))}
                      </ul>

                      <button
                        type="button"
                        onClick={() => onSelect(plan)}
                        className={`mt-6 w-full px-4 py-2 rounded-md font-semibold transition ${isSelected
                          ? "bg-green-600 text-white hover:bg-green-700"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                      >
                        {isSelected ? "Selected" : "Select Plan"}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {!isLoading && !isError && plans.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No plans available at the moment.
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Done
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlansModal;